import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "./spinner/Spinner";
import { ImageIcon } from "./ImageIcon";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdfjs/pdf.worker.min.mjs";

interface ReportCardProps {
  reportContents: string;
  reportTitle: string;
  onRefresh: () => void;
  onDownload: () => void;
  loading: boolean;
}

const base64ToBlob = (base64: string, mimeType = "") => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

export default function ReportCard({
  reportContents,
  reportTitle,
  loading,
  onRefresh,
  onDownload,
}: ReportCardProps) {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;

    if (!reportContents || reportContents.length === 0) {
      setPdfBlobUrl(null);
      return;
    }
    const base64 = reportContents.split(",")[1];
    if (!base64) {
      setPdfBlobUrl(null);
      return;
    }

    if (isNative) {
      // Native: render PDF to canvas using pdfjs
      const renderPdf = async () => {
        const container = canvasContainerRef.current;
        if (!container) return;

        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        pdfDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) {
          pdfDoc.destroy();
          return;
        }

        container.innerHTML = "";

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          if (cancelled) break;

          const page = await pdfDoc.getPage(pageNum);
          if (cancelled) break;

          const scale = 1.5;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          container.appendChild(canvas);

          const context = canvas.getContext("2d");
          if (context && !cancelled) {
            await page.render({ canvasContext: context, viewport, canvas }).promise;
          }
        }
      };

      renderPdf();
    } else {
      // Web: use blob URL for iframe
      const blob = base64ToBlob(base64, "application/pdf");
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setPdfBlobUrl(null);
      };
    }

    return () => {
      cancelled = true;
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [reportContents, isNative]);

  const getPdfViewer = () => {
    // Only used for web - native uses the always-mounted canvas container below
    if (reportContents.length === 0) return null;

    return (
      <div style={{ height: "400px" }}>
        {pdfBlobUrl ? <iframe key={pdfBlobUrl} src={pdfBlobUrl} className="w-full h-full" /> : null}
      </div>
    );
  };

  const sharePDF = async () => {
    const base64 = reportContents.split(",")[1]; // Removes "data:application/pdf;base64,"

    if (Capacitor.isNativePlatform()) {
      const fileName = "immunization_report.pdf";
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
      });

      try {
        await Share.share({
          title: "Share Immunization Report",
          url: result.uri,
        });
      } finally {
        await Filesystem.deleteFile({
          path: fileName,
          directory: Directory.Cache,
        });
      }
    } else {
      const blob = base64ToBlob(base64, "application/pdf");
      const file = new File([blob], "immunization_report.pdf", { type: blob.type });
      navigator
        .share({
          title: "Share Immunization Report",
          files: [file],
        })
        .catch((error) => {});
    }
  };

  return (
    <>
      <div className="rounded-box  border-primary border-2 bg-base-100 dark:bg-base-200 p-4 flex flex-col gap-2">
        <>
          <div className="flex">
            <p className="title font-semibold" data-testid="search-card-name">
              {reportTitle}
            </p>
          </div>
          {loading ? (
            <span className="loading loading-spinner loading-md"></span>
          ) : (
            <>
              {reportContents.length > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <button className="btn btn-primary md:hidden" onClick={onRefresh}>
                      <ImageIcon className="w-4 h-4" src="/assets/images/arrows_rotate_solid.svg" />
                      {t("generic.refresh")}
                    </button>
                    {typeof navigator.share === "function" && (
                      <button onClick={sharePDF} className="btn w-fit btn-primary">
                        <ImageIcon src="/assets/images/share-light.svg" className="w-4 h-4" />
                        {t("immunizations.reports_share")}
                      </button>
                    )}
                    <button onClick={onRefresh} className="max-md:hidden btn  btn-primary w-fit">
                      <ImageIcon className="w-4 h-4" src="/assets/images/arrows_rotate_solid.svg" />
                      {t("generic.refresh")}
                    </button>
                  </div>
                </>
              ) : (
                <button onClick={onDownload} className="btn btn-primary w-fit">
                  {t("immunizations.reports_download")}
                </button>
              )}
              {!isNative && getPdfViewer()}
            </>
          )}
        </>
        {/* Always render canvas container for native so ref is stable */}
        {isNative && (
          <div
            ref={canvasContainerRef}
            style={{
              height: !loading && reportContents.length > 0 ? "400px" : "0px",
              overflowY: "auto",
            }}
          />
        )}
      </div>
    </>
  );
}
